/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { MessageCircle, FileText, Shield, Mail, Settings, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/api/axios';
import { toast } from 'react-toastify';
import { FaWhatsapp } from "react-icons/fa";
import { FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import whatsappService from '../services/whatsappService';
import { useMetaSdk } from '../hooks/useMetaSdk';
import { CloseButton, EditButton, PrimaryButton, ViewButton } from './Buttons';
import CustomInput from './CustomInput';
import { encryptForTransit, decryptFromTransit } from '../utils/common';

function Onboarding() {
  const { userInfo, setUserInfo } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('documents');

  // Handle URL hash navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      // Check for onboarding- prefix
      if (hash && hash.startsWith('onboarding-')) {
        const tabId = hash.replace('onboarding-', '');
        if (tabs.some(tab => tab.id === tabId)) {
          setActiveTab(tabId);
        }
      }
    };

    // Set initial tab based on hash
    handleHashChange();

    // Add event listener for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    window.location.hash = `onboarding-${tabId}`;
  };
  const [isSavingWhatsapp, setIsSavingWhatsapp] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingSSL, setIsCreatingSSL] = useState(false);
  const [isCreatingEmailSecret, setIsCreatingEmailSecret] = useState(false);

  // Project Selection Modal State
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // WhatsApp SDK Connection State
  const { fbReady, error: fbError } = useMetaSdk();
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [fbStatus, setFbStatus] = useState(null);

  const [waData, setWaData] = useState({
    waba_id: '',
    phone_number_id: '',
    business_id: '',
    exchangeable_code: '',
  });

  // WhatsApp Configuration State
  const [whatsappConfig, setWhatsappConfig] = useState({
    metaId: userInfo?.loginIdentifierDetails?.customer?.metaId || "",
    whatsappSecretAccessToken: userInfo?.loginIdentifierDetails?.customer?.whatsappSecretAccessToken || "",
    whatsappBusinessAccountId: userInfo?.loginIdentifierDetails?.customer?.whatsappBusinessAccountId || "",
    whatsappAppId: userInfo?.loginIdentifierDetails?.customer?.whatsappAppId || "",
  });

  const [whatsappVisible, setWhatsappVisible] = useState({
    metaId: false,
    whatsappSecretAccessToken: false,
    whatsappBusinessAccountId: false,
    whatsappAppId: false,
  });

  // Header Footer Stamp Letter State
  const [headerFooterFiles, setHeaderFooterFiles] = useState({
    header: null,
    footer: null,
    stamp: null,
    letter: null,
  });

  // SSL Key State
  const [sslConfig, setSslConfig] = useState({
    sslPassword: '',
    commonName: '',
   // dnsNames: '', // comma separated string for input, will convert to array
    countryCode: 'IN',
  });
 
  const [showSslPassword, setShowSslPassword] = useState(false);
  const [showEmailSecret, setShowEmailSecret] = useState(false); // Email secret visibility toggle
 

  // Email Password Secret State
  const [emailSecretConfig, setEmailSecretConfig] = useState({
    emailPasswordSecret: '',
    emailId: '',
  });
  // Project-wise email config state
  const [emailProjects, setEmailProjects] = useState([]);
  const [selectedEmailProject, setSelectedEmailProject] = useState(null);
  const [projectEmailConfig, setProjectEmailConfig] = useState(null);
  const [loadingEmailProjects, setLoadingEmailProjects] = useState(false);
  const [loadingProjectEmailConfig, setLoadingProjectEmailConfig] = useState(false);

  // Remove: selectedEmailProject, projectEmailConfig, loadingProjectEmailConfig, emailSecretConfig for single project
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editForm, setEditForm] = useState({ emailId: '', emailSecret: '' });
  const [savingProjectId, setSavingProjectId] = useState(null);

  // Add modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [modalProject, setModalProject] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [realPassword, setRealPassword] = useState(''); // Store the real password

  const openEmailModal = (project) => {
 
    
    const emailConfig = project.socialMediaConfiguration?.email || {};
    const decryptedPassword = decryptFromTransit(emailConfig.emailPasswordSecret) || '';
    setRealPassword(decryptedPassword);

    setEditForm({
      emailId: emailConfig.emailId || '',
      emailSecret: decryptedPassword ? '••••••••••••' : ''
    });
    setModalProject(project);
    setShowPassword(false); // Reset password visibility
    

    setTimeout(() => {
      setShowEmailModal(true);
    }, 100);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setModalProject(null);
    setEditForm({ emailId: '', emailSecret: '' });
    setShowPassword(false); // Reset password visibility
    setRealPassword(''); // Reset real password
  };

  const handleModalSave = async () => {
    if (!modalProject) return;
    setSavingProjectId(modalProject.projectId || modalProject.id);
    try {
      // Check if user entered a new password or kept the existing one
      const existingPassword = decryptFromTransit(modalProject.socialMediaConfiguration?.email?.emailPasswordSecret) || '';
      const newPassword = editForm.emailSecret;

      // If user entered the same masked password, use the existing password
      // If user entered a new password, use that
      // If user is showing the real password and modified it, use the real password
      let passwordToSave;
      if (newPassword === '••••••••••••' && existingPassword) {
        passwordToSave = existingPassword;
      } else if (showPassword && realPassword !== existingPassword) {
        passwordToSave = realPassword;
      } else {
        passwordToSave = newPassword;
      }

      const payload = {
        emailId: editForm.emailId,
        emailPasswordSecret: encryptForTransit(passwordToSave),
      };
      await axiosInstance.post(`/projects/email-configuration?projectId=${modalProject.projectId || modalProject.id}`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success('Email secret saved/updated successfully!');
      // Refetch projects to update table
      setLoadingEmailProjects(true);
      const res = await axiosInstance.get('/projects', { params: { customerId: userInfo.loginIdentifierDetails.customer.customerId } });
      const projectsArray = res?.items || (Array.isArray(res) ? res : []);
      setEmailProjects(projectsArray);
      window.dispatchEvent(new Event('projectsConfigurationUpdated'));
      closeEmailModal();
    } catch (error) {
      toast.error('Failed to save email secret. Please try again.');
    } finally {
      setSavingProjectId(null);
      setLoadingEmailProjects(false);
    }
  };

  // Fetch projects for email config (on Email tab open)
  useEffect(() => {
    if (activeTab === 'email' && emailProjects.length === 0) {
      setLoadingEmailProjects(true);
      axiosInstance.get(`/projects?customerId=${userInfo.loginIdentifierDetails.customer.customerId}`)
        .then(res => {
          // Handle both paginated and non-paginated responses
          const projectsArray = res?.items || (Array.isArray(res) ? res : []);
          setEmailProjects(projectsArray);
        })
        .catch(() => toast.error('Failed to fetch projects for email config.'))
        .finally(() => setLoadingEmailProjects(false));
    }
  }, [activeTab, userInfo, emailProjects.length]);

  // Fetch project email config when project changes
  useEffect(() => {
    if (selectedEmailProject) {
      setLoadingProjectEmailConfig(true);
      axiosInstance.get('/projects', { params: { projectId: selectedEmailProject.projectId || selectedEmailProject.id } })
        .then(res => {
          // /api/projects returns an array, so get the first project
          const project = Array.isArray(res) ? res[0] : res;
          const emailConfig = project?.socialMediaConfiguration?.email || {};
          setProjectEmailConfig(emailConfig);
          setEmailSecretConfig({
            emailPasswordSecret: '',
            emailId: emailConfig?.emailId || '',
          });
        })
        .catch(() => {
          setProjectEmailConfig(null);
          setEmailSecretConfig({ emailPasswordSecret: '', emailId: '' });
        })
        .finally(() => setLoadingProjectEmailConfig(false));
    } else {
      setProjectEmailConfig(null);
      setEmailSecretConfig({ emailPasswordSecret: '', emailId: '' });
    }
  }, [selectedEmailProject]);

  // Email Secret Functions (project-wise)
  const handleEmailSecretChange = (e) => {
    const { name, value } = e.target;
    setEmailSecretConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateProjectEmailSecret = async () => {
    if (!selectedEmailProject) {
      toast.error('Please select a project.');
      return;
    }
    setIsCreatingEmailSecret(true);
    try {
      const payload = {
        emailId: emailSecretConfig.emailId,
        emailPasswordSecret: emailSecretConfig.emailPasswordSecret,
      };
      await axiosInstance.post(`/projects/email-configuration?projectId=${selectedEmailProject.projectId || selectedEmailProject.id}`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      toast.success(projectEmailConfig ? 'Email secret updated successfully!' : 'Email secret saved successfully!');
      // Refetch config
      setLoadingProjectEmailConfig(true);
      const res = await axiosInstance.get(`/projects/email-configuration?projectId=${selectedEmailProject.projectId || selectedEmailProject.id}`);
      setProjectEmailConfig(res || null);
      setEmailSecretConfig({ emailPasswordSecret: '', emailId: res?.emailId || '' });
      window.dispatchEvent(new Event('projectsConfigurationUpdated'));
    } catch (error) {
      toast.error('Failed to save email secret. Please try again.');
    } finally {
      setIsCreatingEmailSecret(false);
      setLoadingProjectEmailConfig(false);
    }
  };

  // Company Documents Upload State
  const [companyDocs, setCompanyDocs] = useState({
    letterhead: null,
    footer: null,
    stamp: null,
  });
  const [isUploadingCompanyDocs, setIsUploadingCompanyDocs] = useState(false);
  const [isUploadingSingleDoc, setIsUploadingSingleDoc] = useState({ letterhead: false, footer: false, stamp: false });
  // Previews of the *currently selected* local files (object URLs — revoked on change/unmount).
  const [localDocPreviews, setLocalDocPreviews] = useState({ letterhead: '', footer: '', stamp: '' });
  // Previews of the images *already uploaded* to S3 (presigned URLs from GET /customers/letter-head-stamp).
  const [serverDocPreviews, setServerDocPreviews] = useState({ letterhead: '', footer: '', stamp: '' });
  const [isLoadingDocPreviews, setIsLoadingDocPreviews] = useState(false);

  const tabs = [
  
    { id: 'documents', label: 'Company Documents', icon: FileText },
    { id: 'ssl', label: 'SSL Key Creation', icon: Shield },
    { id: 'email', label: 'Email Password Secret', icon: Mail },
    { id: 'whatsapp', label: 'WhatsApp Integration', icon: MessageCircle },
  ];


  // Check localStorage on mount for WhatsApp connection
  useEffect(() => {
    const wasConnected = localStorage.getItem("wa_connected");
    const savedWaData = localStorage.getItem("wa_data");

    if (wasConnected === "true") {
      setIsConnected(true);
      setCurrentStep(4);
    }

    if (savedWaData) {
      try {
        setWaData(JSON.parse(savedWaData));
      } catch (e) {
        console.error('Failed to parse saved WhatsApp data:', e);
      }
    }
  }, []);


  const steps = [
    {
      title: "Initialize Facebook SDK",
      description: "Setting up the Facebook SDK for WhatsApp integration",
      status: fbReady ? "completed" : "current"
    },
    {
      title: "Connect Facebook Account",
      description: "Log in to your Facebook account to proceed",
      status: currentStep > 1 ? "completed" : "pending"
    },
    {
      title: "Verify PIN",
      description: "Enter the verification PIN sent by Meta",
      status: currentStep > 2 ? "completed" : "pending"
    },
    {
      title: "Setup Complete",
      description: "Your WhatsApp Business API is now connected",
      status: isConnected ? "completed" : "pending"
    }
  ];

  const getStepIcon = (status) => {
    switch (status) {
      case "completed":
        return <FaCheckCircle className="w-5 h-5 text-flamingo-500" />;
      case "current":
        return <FaSpinner className="w-5 h-5 text-flamingo-500 animate-spin" />;
      case "pending":
        return <FaTimesCircle className="w-5 h-5 text-gray-400" />;
      default:
        return null;
    }
  };

  // Document Upload Functions
  const handleFileUpload = (type, file) => {
    setHeaderFooterFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const handleSingleDocumentUpload = async (type) => {
    const file = headerFooterFiles[type];
    if (!file) {
      toast.error('Please select a file first.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append(type === 'header' ? 'letterhead_image' : 
                     type === 'footer' ? 'footer_image' : 
                     type === 'stamp' ? 'stamp_image' : 'letterhead_image', file);
      
      // Add customer ID to form data
      formData.append('customerId', userInfo.loginIdentifierDetails.customer.customerId);

      await axiosInstance.put(`/customers?customerId=${userInfo.loginIdentifierDetails.customer.customerId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
      
      // Clear only the uploaded file
      setHeaderFooterFiles(prev => ({
        ...prev,
        [type]: null
      }));
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      toast.error(`Failed to upload ${type}. Please try again.`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handler for company docs file input.
  // Also (re)builds the local object-URL preview so the user can see the image
  // they picked, *before* clicking Upload. The previous object URL for that
  // slot is revoked to avoid leaking blobs.
  const handleCompanyDocChange = (type, file) => {
    setCompanyDocs(prev => ({ ...prev, [type]: file }));
    setLocalDocPreviews(prev => {
      if (prev[type]) URL.revokeObjectURL(prev[type]);
      return { ...prev, [type]: file ? URL.createObjectURL(file) : '' };
    });
  };

  // Fetch presigned URLs for the already-uploaded letterhead / footer / stamp.
  // Basenames on S3 are header.* / footer.* / stamp_image.* — match by regex.
  const fetchServerDocPreviews = async () => {
    const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
    const isUploaded = userInfo?.loginIdentifierDetails?.customer?.isLetterheadFooterStampUploaded;
    if (!customerId || !isUploaded) return;
    setIsLoadingDocPreviews(true);
    try {
      const resp = await axiosInstance.get(`/customers/letter-head-stamp?customer_id=${customerId}`);
      const arr = Array.isArray(resp) ? resp : [];
      const match = (re) => arr.find(o => re.test(o.key || ''));
      setServerDocPreviews({
        letterhead: match(/^header\./i)?.url || '',
        footer: match(/^footer\./i)?.url || '',
        stamp: match(/^stamp_image\./i)?.url || '',
      });
    } catch (e) {
      console.error('Failed to load uploaded document previews:', e);
    } finally {
      setIsLoadingDocPreviews(false);
    }
  };

  // Load server previews when the Documents tab is opened (and re-load whenever
  // the "is uploaded" flag flips true after a fresh upload).
  useEffect(() => {
    if (activeTab === 'documents') fetchServerDocPreviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userInfo?.loginIdentifierDetails?.customer?.isLetterheadFooterStampUploaded]);

  // Revoke any outstanding local object URLs on unmount to avoid memory leaks.
  useEffect(() => {
    return () => {
      Object.values(localDocPreviews).forEach(u => { if (u) URL.revokeObjectURL(u); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Upload all company docs together
  const handleUploadCompanyDocs = async () => {
    if (!companyDocs.letterhead || !companyDocs.footer || !companyDocs.stamp) {
      toast.error('All three documents are required.');
      return;
    }
    setIsUploadingCompanyDocs(true);
    try {
      const formData = new FormData();
      formData.append('letterhead_image', companyDocs.letterhead);
      formData.append('footer_image', companyDocs.footer);
      formData.append('stamp_image', companyDocs.stamp);
      const response = await axiosInstance.post(
        `/customers/letterhead-footer-stamp/${userInfo.loginIdentifierDetails.customer.customerId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
     

      // Update user context immediately after successful upload
      setUserInfo(prevUserInfo => ({
        ...prevUserInfo,
        loginIdentifierDetails: {
          ...prevUserInfo.loginIdentifierDetails,
          customer: {
            ...prevUserInfo.loginIdentifierDetails.customer,
            isLetterheadFooterStampUploaded: true
          }
        }
      }));

      toast.success('Company documents uploaded successfully!');
      // Clear local file selections and their object-URL previews — the
      // server previews fetched next will replace them.
      setCompanyDocs({ letterhead: null, footer: null, stamp: null });
      setLocalDocPreviews(prev => {
        Object.values(prev).forEach(u => { if (u) URL.revokeObjectURL(u); });
        return { letterhead: '', footer: '', stamp: '' };
      });
      await fetchServerDocPreviews();
    } catch (error) {
      toast.error('Failed to upload company documents. Please try again.');
    } finally {
      setIsUploadingCompanyDocs(false);
    }
  };

  // Upload a single company doc (PUT)
  const handleUploadSingleDoc = async (type) => {
    if (!companyDocs[type]) {
      toast.error('Please select a file first.');
      return;
    }
    setIsUploadingSingleDoc(prev => ({ ...prev, [type]: true }));
    try {
      const formData = new FormData();
      if (type === 'letterhead') formData.append('letterhead_image', companyDocs[type]);
      if (type === 'footer') formData.append('footer_image', companyDocs[type]);
      if (type === 'stamp') formData.append('stamp_image', companyDocs[type]);
      formData.append('customerId', userInfo.loginIdentifierDetails.customer.customerId);
      await axiosInstance.put(`/customers?customerId=${userInfo.loginIdentifierDetails.customer.customerId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update user context immediately after successful upload
      setUserInfo(prevUserInfo => {
        const updatedUserInfo = {
          ...prevUserInfo,
          loginIdentifierDetails: {
            ...prevUserInfo.loginIdentifierDetails,
            customer: {
              ...prevUserInfo.loginIdentifierDetails.customer,
              isLetterheadFooterStampUploaded: true
            }
          }
        };
        console.log("User context updated after single doc upload:", updatedUserInfo);
        return updatedUserInfo;
      });

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
      setCompanyDocs(prev => ({ ...prev, [type]: null }));
      // Drop the local preview for this slot — server preview takes over.
      setLocalDocPreviews(prev => {
        if (prev[type]) URL.revokeObjectURL(prev[type]);
        return { ...prev, [type]: '' };
      });
      await fetchServerDocPreviews();
    } catch (error) {
      toast.error(`Failed to upload ${type}. Please try again.`);
    } finally {
      setIsUploadingSingleDoc(prev => ({ ...prev, [type]: false }));
    }
  };

  // SSL Key Functions
  const handleSSLConfigChange = (e) => {
    const { name, value } = e.target;
    setSslConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateSSLKey = async () => {
    setIsCreatingSSL(true);
    try {
      const payload = {
        customerId: userInfo.loginIdentifierDetails.customer.customerId,
        sslPassword: sslConfig.sslPassword,
        commonName: sslConfig.commonName,
        // dnsNames: sslConfig.dnsNames.split(',').map(s => s.trim()).filter(Boolean),
        countryCode: sslConfig.countryCode,
      };
      const response = await axiosInstance.post('customers/ssl', payload, {
        headers: { 'Content-Type': 'application/json' }
      });
    
      setUserInfo(prevUserInfo => ({
        ...prevUserInfo,
        loginIdentifierDetails: {
          ...prevUserInfo.loginIdentifierDetails,
          customer: {
            ...prevUserInfo.loginIdentifierDetails.customer,
            isSslCertificateGenerated: true
          }
        }
      }));

      toast.success('SSL certificate created successfully!');
      setSslConfig({ sslPassword: '', commonName: '', countryCode: 'IN' });
    } catch (error) {
      toast.error('Failed to create SSL certificate. Please try again.');
    } finally {
      setIsCreatingSSL(false);
    }
  };

  // Project Selection Functions
  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const params = {
        customerId: userInfo.loginIdentifierDetails.customer.customerId
      };
      const response = await axiosInstance.get('/projects', { params });
      // Handle both paginated and non-paginated responses
      const projectsArray = response?.items || (Array.isArray(response) ? response : []);
      setProjects(projectsArray);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to fetch projects. Please try again.');
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleConnectWhatsApp = () => {
    setShowProjectModal(true);
    fetchProjects();
  };

  const handleProjectSelect = (project) => {
  
    setSelectedProject(project);
  };

  const handleConfirmProject = () => {
    if (selectedProject) {
      setShowProjectModal(false);
      navigate('/whatsapp/setup', { state: { selectedProject } });
    } else {
      toast.error('Please select a project first.');
    }
  };

  const renderWhatsAppTab = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Connect Your WhatsApp Business Account</h3>
        <p className="text-gray-600 text-sm">
          Integrate your WhatsApp Business account with BuildVizer to enable automated messaging and personalized communication.
        </p>
      </div>

      {/* Integration Steps */}
      <div className=" rounded-lg p-4 mb-6">
        <h4 className="text-md font-medium text-gray-800 mb-3">Integration Steps</h4>
        <ol className="space-y-3 text-sm">
          <li className="flex items-start">
            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs">1</span>
            <span className="ml-3 text-gray-600">Create a Meta Business Account and set up WhatsApp Business API</span>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs">2</span>
            <span className="ml-3 text-gray-600">Get your Meta ID, WhatsApp Business Account ID, and App ID</span>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs">3</span>
            <span className="ml-3 text-gray-600">Generate your WhatsApp Secret Access Token</span>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs">4</span>
            <span className="ml-3 text-gray-600">Configure your WhatsApp settings in BuildVizer</span>
          </li>
        </ol>
      </div>

      {/* Connect Button */}
      <div className="flex justify-center mb-6">
        <button
          onClick={handleConnectWhatsApp}
          className="flex items-center space-x-2 px-6 py-3 bg-flamingo-500 hover:bg-flamingo-600 text-white rounded-lg transition-colors shadow-md text-sm font-medium"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span>Connect with WhatsApp</span>
        </button>
      </div>

      {/* SDK Connection Status */}
      {currentStep > 1 && (
        <div className="bg-white rounded-lg p-4 border mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <FaWhatsapp className="w-6 h-6 text-[#25D366]" />
              <h4 className="text-md font-medium text-gray-800">Connection Progress</h4>
            </div>
          </div>

          {/* Connection Steps */}
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={index} className={`p-3 rounded-lg border ${
                step.status === 'completed' ? 'bg-flamingo-50 border-flamingo-200' :
                step.status === 'current' ? 'bg-slate-50 border-slate-200' :
                'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-start gap-3">
                  {getStepIcon(step.status)}
                  <div>
                    <h5 className="text-sm font-medium text-gray-800">{step.title}</h5>
                    <p className="text-xs text-gray-600">{step.description}</p>
                  </div>
                </div>
                {step.status === "current" && fbStatus && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-gray-700">
                        Facebook Status: <span className="font-medium">{fbStatus.status}</span>
                      </p>
                      {fbStatus.authResponse && (
                        <p className="text-xs text-gray-700">
                          User ID: <span className="font-medium">{fbStatus.authResponse.userID}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Status Messages */}
          {isConnected && (
            <div className="mt-4 p-3 flex items-center gap-3 bg-flamingo-50 rounded-lg">
              <FaCheckCircle className="w-5 h-5 text-flamingo-500" />
              <div>
                <h5 className="text-sm font-medium text-flamingo-800">Connected Successfully!</h5>
                <p className="text-xs text-flamingo-600">Your WhatsApp Business API is now connected and ready to use.</p>
              </div>
            </div>
          )}

          {fbError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {fbError}
            </div>
          )}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 p-3 bg-flamingo-50 border border-flamingo-200 rounded-lg text-flamingo-700 text-sm">
              {success}
            </div>
          )}
        </div>
      )}

      {/* Manual Configuration Section */}
      {/* <div className="bg-white rounded-lg p-4 border">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-medium text-gray-800">Manual Configuration</h4>
          <button
            className="text-flamingo-600 font-medium text-sm"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? "Cancel" : "Edit"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { id: "metaId", label: "Meta ID", description: "Unique identifier for your Meta Business Portfolio." },
            { id: "whatsappSecretAccessToken", label: "WhatsApp Secret Access Token", description: "Secret token for secure access to your WhatsApp Business API." },
            { id: "whatsappBusinessAccountId", label: "WhatsApp Business Account ID", description: "Your WhatsApp Business Account ID." },
            { id: "whatsappAppId", label: "WhatsApp App ID", description: "Your WhatsApp Business App ID for API integration." }
          ].map(({ id, label, description }) => (
            <div key={id}>
              <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
              <div className="relative flex items-center">
                {!isEditing ? (
                  <textarea
                    id={id}
                    className="h-8 mt-1 flex items-center bg-gray-50 px-3 rounded-md text-gray-800 border border-gray-200 w-full text-ellipsis text-sm"
                    style={{ minHeight: 32, maxHeight: 80, resize: 'none' }}
                    value={whatsappVisible[id] ? (whatsappConfig[id] || '') : (whatsappConfig[id] ? '••••••••' : '')}
                    readOnly
                    ref={el => autoResize(el)}
                  />
                ) : (
                  <textarea
                    id={id}
                    name={id}
                    value={whatsappConfig[id]}
                    onChange={e => { handleWhatsappConfigChange(e); autoResize(e.target); }}
                    className="h-8 w-full mt-1 rounded-lg border-gray-300 focus:ring-2 focus:ring-flamingo-500 text-ellipsis text-sm"
                    style={{ minHeight: 32, maxHeight: 80, resize: 'none' }}
                    ref={el => autoResize(el)}
                  />
                )}
                <button
                  type="button"
                  className="absolute right-2 top-1 text-gray-500 hover:text-gray-700"
                  onClick={() => toggleWhatsappVisibility(id)}
                  tabIndex={-1}
                >
                  {whatsappVisible[id] ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{description}</p>
            </div>
          ))}
        </div>

        {isEditing && (
          <div className="mt-6 flex w-full justify-end">
            <button
              className="bg-flamingo-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-flamingo-600 transition text-sm"
              onClick={handleSaveWhatsAppConfig}
              disabled={isSavingWhatsapp}
            >
              {isSavingWhatsapp ? "Saving..." : "Save Configuration"}
            </button>
          </div>
        )}
      </div> */}

      {/* PIN Modal */}
      {/* {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Enter 6-digit PIN</h3>
              <button
                className="text-gray-400 hover:text-gray-600 text-2xl"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4 text-sm">
                Meta just sent you a verification PIN. Enter it below to finalise the connection.
              </p>
              <div className="mb-4">
                <input
                  type="text"
                  maxLength={6}
                  value={pin}
                  onChange={handlePinChange}
                  placeholder="Enter PIN"
                  className="w-full px-3 py-2 text-lg text-center tracking-widest border-2 border-gray-200 rounded-lg focus:border-flamingo-500 focus:outline-none"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 text-right">
              <button
                className={`px-4 py-2 rounded-lg font-semibold text-white transition-colors text-sm
                  ${(!pin || pin.length !== 6 || loading) 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-flamingo-500 hover:bg-flamingo-600'}`}
                onClick={handleSubmitPin}
                disabled={!pin || pin.length !== 6 || loading}
              >
                {loading ? 'Verifying...' : 'Verify & Connect'}
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );

  const renderDocumentsTab = () => {
    const isUploaded = userInfo?.loginIdentifierDetails?.customer.isLetterheadFooterStampUploaded;
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Upload Your Company Documents</h3>
          <p className="text-gray-600 text-sm">
            Upload your company header, footer, and stamp to customize your documents.
          </p>
        </div>
        {isUploaded && (
          <div className="bg-flamingo-50 border border-flamingo-200 rounded-lg p-3 text-center mt-6 flex flex-col items-center">
            
            <h4 className="text-sm font-medium text-flamingo-800 mb-0">Letterhead, Footer, and Stamp already uploaded!</h4>
          </div>
        )}
        {/* Renders the dropzone for a single slot. Shows (in order of priority):
              1. the local preview if the user just picked a file,
              2. the server preview if one has already been uploaded,
              3. otherwise the empty "Click to upload" placeholder.
            Used in both states (already-uploaded and first-time upload). */}
        {isUploaded ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-between">
            {['letterhead', 'footer', 'stamp'].map((key) => {
              const preview = localDocPreviews[key] || serverDocPreviews[key];
              const isLocal = !!localDocPreviews[key];
              return (
              <div key={key} className=" rounded-lg p-4 h-80 flex flex-col">
                <h4 className="text-md font-medium text-gray-800 mb-2">{key.charAt(0).toUpperCase() + key.slice(1)} Image</h4>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center flex-1 flex items-center justify-center overflow-hidden relative">
                  <input
                    type="file"
                    id={`company-doc-${key}`}
                    className="hidden"
                    accept="image/*"
                    onChange={e => handleCompanyDocChange(key, e.target.files[0])}
                  />
                  <label htmlFor={`company-doc-${key}`} className="cursor-pointer w-full h-full flex items-center justify-center">
                    {preview ? (
                      <>
                        <img
                          src={preview}
                          alt={`${key} preview`}
                          className="max-h-full max-w-full object-contain"
                        />
                        <span className="absolute bottom-1 right-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-white/90 text-gray-700 border border-gray-200">
                          {isLocal ? 'New (not yet uploaded)' : 'Uploaded'}
                        </span>
                      </>
                    ) : isLoadingDocPreviews ? (
                      <span className="text-xs text-gray-500">Loading preview…</span>
                    ) : (
                      <div className="space-y-2">
                        <FileText className="mx-auto h-8 w-8 text-gray-400" />
                        <div className="text-xs text-gray-600">
                          <span className="font-medium text-flamingo-600 hover:text-flamingo-500">
                            Click to replace
                          </span>
                        </div>
                      </div>
                    )}
                  </label>
                </div>
                {companyDocs[key] && (
                  <div className="mt-2 p-1.5 bg-flamingo-50 rounded-lg">
                    <p className="text-xs text-flamingo-800 truncate">
                      ✓ {companyDocs[key].name}
                    </p>
                  </div>
                )}
                <div className="mt-3">
                  <PrimaryButton
                    onClick={() => handleUploadSingleDoc(key)}
                    disabled={isUploadingSingleDoc[key] || !companyDocs[key]}
                    className="w-full shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {isUploadingSingleDoc[key] ? 'Uploading...' : `Update ${key.charAt(0).toUpperCase() + key.slice(1)}`}
                  </PrimaryButton>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <form
            onSubmit={e => { e.preventDefault(); handleUploadCompanyDocs(); }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-between"
          >
            {[
              { key: 'letterhead', label: 'Letterhead Image', accept: 'image/*', required: true },
              { key: 'footer', label: 'Footer Image', accept: 'image/*', required: true },
              { key: 'stamp', label: 'Stamp Image', accept: 'image/*', required: true },
            ].map(({ key, label, accept, required }) => {
              const preview = localDocPreviews[key];
              return (
              <div key={key} className="bg-gray-50 rounded-lg p-4 h-80 flex flex-col">
                <h4 className="text-md font-medium text-gray-800 mb-2">{label}</h4>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center flex-1 flex items-center justify-center overflow-hidden">
                  <input
                    type="file"
                    id={`company-doc-${key}`}
                    className="hidden"
                    accept={accept}
                    required={required}
                    onChange={e => handleCompanyDocChange(key, e.target.files[0])}
                  />
                  <label htmlFor={`company-doc-${key}`} className="cursor-pointer w-full h-full flex items-center justify-center">
                    {preview ? (
                      <img
                        src={preview}
                        alt={`${key} preview`}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <div className="space-y-2">
                        <FileText className="mx-auto h-8 w-8 text-gray-400" />
                        <div className="text-xs text-gray-600">
                          <span className="font-medium text-flamingo-600 hover:text-flamingo-500">
                            Click to upload
                          </span>
                          {' '}or drag and drop
                        </div>
                      </div>
                    )}
                  </label>
                </div>
                {companyDocs[key] && (
                  <div className="mt-2 p-1.5 bg-flamingo-50 rounded-lg">
                    <p className="text-xs text-flamingo-800 truncate">
                      ✓ {companyDocs[key].name}
                    </p>
                  </div>
                )}
              </div>
              );
            })}
            <div className="md:col-span-3 flex justify-center mt-6">
              <PrimaryButton
                type="submit"
                disabled={isUploadingCompanyDocs || !companyDocs.letterhead || !companyDocs.footer || !companyDocs.stamp}
                className="w-full max-w-xs shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isUploadingCompanyDocs ? 'Uploading...' : 'Upload All Documents'}
              </PrimaryButton>
            </div>
          </form>
        )}
      </div>
    );
  };

  const renderSSLTab = () => {
    const isSslGenerated = userInfo?.loginIdentifierDetails?.customer.isSslCertificateGenerated;
    if (isSslGenerated) {
      return (
        <div className="max-w-2xl mx-auto">
          
          <div className="bg-flamingo-50 border border-flamingo-200 rounded-lg p-6 text-center mt-8">
            <h4 className="text-md font-medium text-flamingo-800 mb-2">SSL certificate already generated!</h4>
            <p className="text-flamingo-700 text-sm">Your SSL certificate is already on file.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Create SSL Certificate</h3>
          <p className="text-gray-600 text-sm">
            Generate SSL certificates for secure communication and data encryption.
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-4">
            <div>
              <div className="relative">
                <CustomInput
                  type={showSslPassword ? "text" : "password"}
                  name="sslPassword"
                  label="SSL Password"
                  value={sslConfig.sslPassword}
                  onChange={handleSSLConfigChange}
                  placeholder="Enter SSL password"
                />
                <button
                  type="button"
                  className="absolute right-2 top-12 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowSslPassword((prev) => !prev)}
                  tabIndex={-1}
                >
                  {showSslPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-sm font-medium text-orange-500 mt-1 flex items-center">
                <span className="mr-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="inline-block h-4 w-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01"/>
                  </svg>
                </span>
                Note: This password will not be saved. Please remember it for future use.
              </p>
            </div>
            <div>
              <CustomInput
                type="text"
                label="Common Name"
                name="commonName"
                value={sslConfig.commonName}
                onChange={handleSSLConfigChange}
                placeholder="e.g. example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country Code</label>
              <select
                name="countryCode"
                value={sslConfig.countryCode}
                onChange={handleSSLConfigChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-flamingo-500 focus:border-flamingo-500 text-sm"
              >
                <option value="IN">India (IN)</option>
                <option value="US">United States (US)</option>
                <option value="GB">United Kingdom (GB)</option>
                <option value="CA">Canada (CA)</option>
                <option value="AU">Australia (AU)</option>
              </select>
            </div>
            <div className="flex justify-center">
              <PrimaryButton
                onClick={handleCreateSSLKey}
                disabled={isCreatingSSL || !sslConfig.sslPassword || !sslConfig.commonName}
                className=" w-1/2 shadow-md  transition disabled:opacity-50 disabled:cursor-not-allowed "
              >
                {isCreatingSSL ? "Creating..." : "Create SSL Certificate"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleEditClick = (project) => {
    setEditingProjectId(project.projectId || project.id);
    setEditForm({
      emailId: project.socialMediaConfiguration?.email?.emailId || '',
      emailSecret: ''
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
    
    // If it's the password field and we're showing the real password, update it
    if (name === 'emailSecret' && showPassword) {
      setRealPassword(value);
    }
  };

  const handleEditSave = async (projectId) => {
    setSavingProjectId(projectId);
    try {
      const payload = {
        emailId: editForm.emailId,
        emailPasswordSecret: encryptForTransit(editForm.emailSecret),
      };
      await axiosInstance.post(`/projects/email-configuration?projectId=${projectId}`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success('Email secret saved/updated successfully!');
      // Refetch projects to update table
      setLoadingEmailProjects(true);
      const res = await axiosInstance.get('/projects', { params: { customerId: userInfo.loginIdentifierDetails.customer.customerId } });
      const projectsArray = res?.items || (Array.isArray(res) ? res : []);
      setEmailProjects(projectsArray);
      setEditingProjectId(null);
      setEditForm({ emailId: '', emailSecret: '' });
      window.dispatchEvent(new Event('projectsConfigurationUpdated'));
    } catch (error) {
      toast.error('Failed to save email secret. Please try again.');
    } finally {
      setSavingProjectId(null);
      setLoadingEmailProjects(false);
    }
  };

  const handleEditCancel = () => {
    setEditingProjectId(null);
    setEditForm({ emailId: '', emailSecret: '' });
  };

  const renderEmailTab = () => (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Email Secret Configuration</h3>
        <p className="text-gray-600 text-sm">
          Set up or update email authentication for each project. You can see which projects have email secrets configured.
        </p>
      </div>
      
      <div className="flex gap-6">
        {/* Left Sidebar - 1/4th width */}
        <div className="w-1/4 bg-gray-50 p-6 rounded-lg border">
          <h5 className="text-sm font-semibold text-gray-800 mb-4">How to Get App Password</h5>
          <div className="space-y-3 text-xs text-gray-600">
            <div className="flex items-start">
              <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs mr-2">1</span>
              <span>Go to your Google Account settings</span>
            </div>
            <div className="flex items-start">
              <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs mr-2">2</span>
              <span>Navigate to Security → 2-Step Verification</span>
            </div>
            <div className="flex items-start">
              <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs mr-2">3</span>
              <span>Scroll down to "App passwords"</span>
            </div>
            <div className="flex items-start">
              <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs mr-2">4</span>
              <span>Select "Mail" and your device</span>
            </div>
            <div className="flex items-start">
              <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 font-medium text-xs mr-2">5</span>
              <span>Copy the 16-character password</span>
            </div>
          </div>
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-800 font-medium">Note:</p>
            <p className="text-xs text-slate-700 mt-1">
              App passwords are 16 characters long and don't include spaces. Use this password instead of your regular Google password.
            </p>
          </div>
        </div>
        
        {/* Right Side - 3/4th width */}
        <div className="w-3/4">
          <div className="bg-white rounded-lg p-4 border">
            {loadingEmailProjects ? (
              <div className="text-gray-500 text-sm">Loading projects...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Project Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {emailProjects.map(project => {
                      const emailConfig = project.socialMediaConfiguration?.email || {};
                      return (
                        <tr key={project.projectId || project.id}>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{project.projectName || project.name}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                            {emailConfig.emailId || <span className="text-gray-400">Not set</span>}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            {emailConfig.emailPasswordSecret
                              ? <span className="text-flamingo-600 font-medium">Configured</span>
                              : <span className="text-red-500 font-medium">Not Configured</span>}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm flex gap-2">
                            {emailConfig.emailPasswordSecret ? (
                              <EditButton
                                onClick={() => openEmailModal(project)}
                                disabled={false}
                                aria-label="Edit Email Config"
                              />
                            ) : (
                              <PrimaryButton
                                onClick={() => openEmailModal(project)}
                                disabled={false}
                                aria-label="Set Email Config"
                                className="!w-auto !px-4 !py-2 !text-xs"
                              >
                                Set
                              </PrimaryButton>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modal for editing/setting email config */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4 p-6 relative">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">
                {modalProject?.projectName || modalProject?.name}
                {modalProject?.socialMediaConfiguration?.email?.emailPasswordSecret ? ' - Edit App Password' : ' - New App Password'}
              </h4>
              <CloseButton className="ml-4" onClick={closeEmailModal} aria-label="Close" type="button">×</CloseButton>
            </div>
            
            {/* Hidden dummy fields to prevent auto-fill */}
            <input type="text" style={{ display: 'none' }} autoComplete="username" />
            <input type="password" style={{ display: 'none' }} autoComplete="current-password" />
            
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">Email ID</label>
              <input
                name="emailId"
                type="email"
                value={editForm.emailId}
                onChange={handleEditChange}
                placeholder="Enter email ID"
                className="w-full border rounded px-3 py-2 text-sm"
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">App Password</label>
              <div className="relative">
                <input
                  name="emailSecret"
                  type={showPassword ? "text" : "password"}
                  value={showPassword ? realPassword : editForm.emailSecret}
                  onChange={handleEditChange}
                  placeholder="Enter app password"
                  className="w-full border rounded px-3 py-2 text-sm"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              
            </div>
            <div className="flex justify-end gap-2 mt-6">
              
              <PrimaryButton
                onClick={handleModalSave}
                disabled={savingProjectId === (modalProject?.projectId || modalProject?.id) || !editForm.emailId || !editForm.emailSecret}
                className="px-4 py-2 text-sm"
              >
                {savingProjectId === (modalProject?.projectId || modalProject?.id) ? 'Saving...' : 'Save'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'whatsapp':
        return renderWhatsAppTab();
      case 'documents':
        return renderDocumentsTab();
      case 'ssl':
        return renderSSLTab();
      case 'email':
        return renderEmailTab();
      default:
        return renderWhatsAppTab();
    }
  };

  const activeIndex = tabs.findIndex((t) => t.id === activeTab);
  const totalTabs = tabs.length;

  return (
    <section className="bg-white shadow-sm border border-gray-200 rounded-2xl overflow-hidden">
      {/* Header — dark slate gradient consistent with every other Settings section */}
      <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-600 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-white">Onboarding Setup</h2>
          <p className="text-xs text-slate-300 mt-0.5">
            Step {activeIndex + 1} of {totalTabs} — {tabs[activeIndex]?.label}
          </p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/20">
          <Settings size={12} />
          Wizard
        </span>
      </div>

      {/* Numbered stepper tabs */}
      <div className="border-b border-gray-200 bg-gray-50/40">
        <div className="flex items-stretch w-full">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`group relative flex items-center justify-center gap-2 flex-1 min-w-0 px-2 sm:px-3 py-3 border-b-2 transition-colors ${
                  isActive
                    ? "border-flamingo-500 text-flamingo-700 bg-white"
                    : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-white"
                }`}
              >
                {Icon && (
                  <Icon
                    size={16}
                    className={`shrink-0 ${isActive ? "text-flamingo-500" : "text-gray-400"}`}
                  />
                )}
                <span className="text-xs sm:text-sm font-medium truncate">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="p-5 sm:p-6 lg:p-8">{renderContent()}</div>

      {/* Project Selection Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <FaWhatsapp className="w-6 h-6 text-[#25D366]" />
                <h3 className="text-xl font-semibold text-gray-800">Select Project for WhatsApp Integration</h3>
              </div>
              <CloseButton
                className="text-gray-400 hover:text-gray-600 text-2xl"
                onClick={() => setShowProjectModal(false)}
              >
                
              </CloseButton>
            </div>
            
            <div className="p-6">
              {loadingProjects ? (
                <div className="flex items-center justify-center py-8">
                  <FaSpinner className="w-6 h-6 text-flamingo-500 animate-spin" />
                  <span className="ml-3 text-gray-600">Loading projects...</span>
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Projects Found</h3>
                  <p className="text-gray-500">You need to create a project first before connecting WhatsApp.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Select a project to connect WhatsApp Business API. This will enable automated messaging for the selected project.
                  </p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Project
                    </label>
                    <select
                      value={selectedProject?.projectId || selectedProject?.id || ''}
                      onChange={(e) => {
                       
                        
                        if (e.target.value) {
                          const project = projects.find(p => {
                            const projectId = String(p.projectId || p.id);
                            const selectedValue = String(e.target.value);
                           
                            return projectId === selectedValue;
                          });
                         
                          handleProjectSelect(project);
                        } else {
                          handleProjectSelect(null);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-flamingo-500 focus:border-flamingo-500 text-sm"
                    >
                      <option value="">Choose a project...</option>
                      {projects.map((project, index) => {
                      
                        return (
                          <option key={project.projectId || project.id || index} value={project.projectId || project.id || index}>
                            {project.projectName || project.name || `Project ${index + 1}`} - {project.projectStatus || project.status || 'Active'}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {selectedProject && (
                    <div className="bg-gray-50 rounded-lg p-4">
                  
                      <div className="space-y-2 text-sm">
                        <div>
                          {selectedProject.projectName || selectedProject.name}
                        </div>
                        
                       
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                className={`px-6 py-2 rounded-lg font-medium text-white transition-colors ${
                  selectedProject
                    ? 'bg-flamingo-500 hover:bg-flamingo-600'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                onClick={handleConfirmProject}
                disabled={!selectedProject}
              >
                Continue to WhatsApp Setup
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Onboarding; 